import {
  Component,
  effect,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import {
  Leg,
  RfqLegSettings,
  ScheduleColumn,
  ScheduleName
} from '@approot/models';
import {
  ScheduleUtilities,
  SubscriptionSink,
  Utilities
} from '@approot/utilities';
import copy from 'copy-to-clipboard';
import { QuoteScheduleService } from './quote-schedule.service';
import { CdkDrag } from '@angular/cdk/drag-drop';
import { WindowIndexService } from '@approot/widgets/rfq/rfq-container/window-index.service';

const WIN_POSITION_MIN_X = 8;
const WIN_POSITION_MIN_Y = 36;
const WIN_POSITION_RIGHT_MIN = 100;

export interface Detail {
  premiumAmountBid: number;
  premiumAmountAsk: number;
  volBid: number;
  volAsk: number;
}

@Component({
  selector: 'app-quote-schedule',
  templateUrl: './quote-schedule.component.html',
  styleUrls: ['./quote-schedule.component.scss'],
  providers: [QuoteScheduleService]
})
export class QuoteScheduleComponent implements OnChanges, OnDestroy {
  @Input() public columns: ScheduleColumn[];
  @Input() public isModalOpened = false;
  @Input() public additionalColumns: any[];
  @Input() public legData: FormGroup;
  @Input() public rfqLegSettings: RfqLegSettings;
  @Input() public scheduleName: ScheduleName;
  @Input() public leg: Leg;
  @Input() public requestId: number;
  @Input() public parentTabIndex: number;
  @Input() public parentLegIndex: number;
  @Output() public scheduleOpenChanged = new EventEmitter<boolean>();
  @ViewChild(CdkDrag) public dragEl: CdkDrag;

  public originalFormData: any;
  public scheduleArray: FormArray;
  private subSink = new SubscriptionSink();
  public details: Detail[] =
    this.quoteScheduleService.message()?.quote?.optionQuoteDetails?.legs[0]
      ?.schedule;
  public dragging = false;
  public quoteSpecificColumns = [
    'Premium Amount Bid',
    'Premium Amound Offer',
    'Vol Bid',
    'Vol Offer'
  ];

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
    public quoteScheduleService: QuoteScheduleService,
    private winIdx: WindowIndexService
  ) {
    effect(() => {
      const newMessage = this.quoteScheduleService.message();
      this.details = newMessage?.quote?.optionQuoteDetails?.legs[0]?.schedule;
    });
    setInterval(() => {
      console.log({
        columns: this.columns,
        isModalOpened: this.isModalOpened,
        additionalColumns: this.additionalColumns,
        legData: this.legData,
        rfqLegSettings: this.rfqLegSettings,
        scheduleName: this.scheduleName,
        leg: this.leg,
        originalFormData: this.originalFormData,
        requestId: this.requestId
      });
    }, 1000);
  }

  public ngOnDestroy(): void {
    this.subSink.unsubscribe();
    this.quoteScheduleService.unsubscribe();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes['columns']?.currentValue) {
      this.columns = changes['columns']?.currentValue.filter(
        x =>
          x.shortName === 'fixingNumber' ||
          x.shortName === 'expiryDate' ||
          x.shortName === 'settlementDate'
      );
    }
    if (changes['requestId']) {
      this.quoteScheduleService.requestId = changes['requestId']?.currentValue;
    }

    if (changes['scheduleName']?.currentValue) {
      console.log('REACTING TO SCHEDULENAME CHANGES');
      const schedule = this.legData
        .get(changes['scheduleName']?.currentValue)
        .value.map((row, index) => {
          const formGroup = ScheduleUtilities.scheduleControlMaker(
            row,
            index,
            this.legData,
            this.rfqLegSettings.rfqLeg,
            this.leg.productType,
            this.columns,
            this.legData.get(changes['scheduleName']?.currentValue) as FormArray
          );

          return formGroup;
        });
      this.scheduleArray = new FormArray(schedule);
      this.originalFormData = this.legData.getRawValue();

      this.columns = this.columns?.filter(
        x =>
          x.shortName === 'fixingNumber' ||
          x.shortName === 'expiryDate' ||
          x.shortName === 'settlementDate'
      );

      this.quoteScheduleService.requestId = this.requestId;
    }
  }

  public updateZIndex() {
    this.winIdx.setWindowIndex(
      this.elementRef.nativeElement.querySelector('.rfq-drag'),
      this.renderer
    );
  }

  // public ngOnInit(): void {
  //   // we want to re-make the form control schedule, as we do not want changes
  //   // to reflect on the leg

  //   const schedule = this.legData
  //     .get(this.scheduleName)
  //     .value.map((row, index) => {
  //       const formGroup = ScheduleUtilities.scheduleControlMaker(
  //         row,
  //         index,
  //         this.legData,
  //         this.rfqLegSettings.rfqLeg,
  //         this.leg.productType,
  //         this.columns,
  //         this.legData.get(this.scheduleName) as FormArray
  //       );

  //       return formGroup;
  //     });
  //   this.scheduleArray = new FormArray(schedule);
  //   this.originalFormData = this.legData.getRawValue();

  //   this.columns = this.columns?.filter(
  //     x =>
  //       x.shortName === 'fixingNumber' ||
  //       x.shortName === 'expiryDate' ||
  //       x.shortName === 'settlementDate'
  //   );

  //   this.quoteScheduleService.requestId = this.requestId;
  //   console.log('init');
  // }

  public get scheduleArrayControls() {
    return this.scheduleArray.controls as FormGroup[];
  }

  public dragStart() {
    this.dragging = true;
  }

  public async dragEnd() {
    this.dragging = false;
    const el = (this.elementRef.nativeElement as HTMLElement).querySelector(
      '.rfq-drag'
    );
    const rect = el.getBoundingClientRect();

    let { x, y } = this.dragEl.getFreeDragPosition();

    if (rect.x < WIN_POSITION_MIN_X) {
      x += WIN_POSITION_MIN_X - rect.x;
    }
    if (rect.y < WIN_POSITION_MIN_Y) {
      y += WIN_POSITION_MIN_Y - rect.y;
    }
    if (rect.x > window.innerWidth - WIN_POSITION_RIGHT_MIN) {
      x -= rect.x - (window.innerWidth - WIN_POSITION_RIGHT_MIN);
    }
    this.dragEl.setFreeDragPosition({ x, y });
  }

  public onCopyToClipboardClicked() {
    const table = this.elementRef.nativeElement.querySelector('#table-data');
    // copy the text. keep in text/plain, so that tabs and new lines stay
    copy(Utilities.tableToExcelFormat(table), { format: 'text/plain' });
  }

  public modalChanged() {
    this.scheduleOpenChanged.emit(false);
  }
}
